

/**
 * RewardsList
 *
 * Fetches GET /api/rewards/available and renders per-category claim rows.
 * Excludes DAILY + REFERRAL (those have their own dedicated components).
 *
 * When the list is empty — meaning every social/content reward is isActive=false
 * in the catalog — we hide the whole section rather than showing empty scaffolding.
 * As admin flips reward types active, they start appearing here automatically.
 */

import { useCallback, useEffect, useState } from 'react';
import { useAuthStore } from '@/stores/auth.store';
import { authFetch } from '@/lib/auth-client';
import { cn } from '@/lib/utils';
import { SubmitURLModal, type SubmitURLModalReward } from './SubmitURLModal';

interface AvailableReward {
  rewardType: string;
  label: string;
  description: string;
  coinsAmount: number;
  category: string;
  cooldownMinutes: number;
  maxPerDay: number | null;
  maxPerUser: number | null;
  metadata: Record<string, unknown> | null;
  cooldownEndsAt: string | null;
  todayCount: number;
  lifetimeCount: number;
  capReached: boolean;
}

const CATEGORY_ORDER = ['ADS', 'SOCIAL', 'CONTENT', 'MILESTONE'] as const;
const CATEGORY_LABEL: Record<string, string> = {
  ADS: 'Watch Ads',
  SOCIAL: 'Social Actions',
  CONTENT: 'Share Your Picks',
  MILESTONE: 'Milestones',
};

function CoinIcon({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="12" cy="12" r="10" />
      <path d="M12 6v12M9 9.5c0-.83 1.34-1.5 3-1.5s3 .67 3 1.5S14.66 11 12 11 9 11.67 9 12.5s1.34 1.5 3 1.5 3-.67 3-1.5" />
    </svg>
  );
}

function ClockIcon({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="12" cy="12" r="10" /><polyline points="12 6 12 12 16 14" />
    </svg>
  );
}

function formatCooldown(endsAt: string): string {
  const ms = new Date(endsAt).getTime() - Date.now();
  if (ms <= 0) return 'ready';
  const hours = Math.floor(ms / 3600000);
  const mins = Math.floor((ms % 3600000) / 60000);
  if (hours >= 24) return `${Math.floor(hours / 24)}d ${hours % 24}h`;
  if (hours > 0) return `${hours}h ${mins}m`;
  return `${mins}m`;
}

function RewardRow({ reward, onClaim, claimingType }: {
  reward: AvailableReward;
  onClaim: (reward: AvailableReward) => void;
  claimingType: string | null;
}) {
  const onCooldown = reward.cooldownEndsAt !== null;
  const capReached = reward.capReached;
  const disabled = onCooldown || capReached || claimingType === reward.rewardType;

  return (
    <div className={cn(
      'flex items-center gap-2 py-1.5 px-2 rounded-lg transition-all',
      disabled ? 'opacity-60' : 'hover:bg-surface-3/30',
    )}>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-1.5">
          <span className="text-[11px] font-semibold text-cream truncate">{reward.label}</span>
          {reward.maxPerDay !== null && (
            <span className="text-[9px] text-warm-gray/30 bg-surface-3 rounded px-1 py-0.5">
              {reward.todayCount}/{reward.maxPerDay} today
            </span>
          )}
        </div>
        <div className="text-[10px] text-warm-gray/50 truncate">{reward.description}</div>
      </div>

      <span className="text-[10px] font-bold text-gold shrink-0">+{reward.coinsAmount}c</span>

      {capReached ? (
        <span className="text-[9px] text-warm-gray/40 italic shrink-0">cap reached</span>
      ) : onCooldown ? (
        <span className="text-[9px] text-warm-gray/50 shrink-0 inline-flex items-center gap-0.5">
          <ClockIcon className="w-3 h-3" />
          {reward.cooldownEndsAt ? formatCooldown(reward.cooldownEndsAt) : ''}
        </span>
      ) : (
        <button
          onClick={() => onClaim(reward)}
          disabled={disabled}
          className="px-2 py-0.5 rounded text-[10px] font-semibold bg-gold/15 text-gold hover:bg-gold/25 border border-gold/30 transition-all whitespace-nowrap"
        >
          {claimingType === reward.rewardType ? '...' : 'Claim'}
        </button>
      )}
    </div>
  );
}

/** Reward catalog metadata fields the UI cares about. Anything else is opaque. */
interface RewardMetadata {
  /** When true, claiming opens the SubmitURLModal instead of POSTing immediately */
  requiresUrl?: boolean;
  /** Platform identifier — drives modal placeholder + host validation */
  platform?: 'twitter' | 'instagram' | 'tiktok' | 'generic';
  /** Optional pre-built compose URL the user can click to open the platform */
  composeUrl?: string;
  /** Optional hint copy displayed in the modal */
  urlHint?: string;
}

/** Read the metadata flags safely — admin can edit JSON; never trust shape. */
function readMetadata(metadata: AvailableReward['metadata']): RewardMetadata {
  if (!metadata || typeof metadata !== 'object') return {};
  return metadata as RewardMetadata;
}

export function RewardsList({ className }: { className?: string }) {
  const updateBalance = useAuthStore((s) => s.updateBalance);
  const userCoins = useAuthStore((s) => s.user?.coins ?? 0);
  const [rewards, setRewards] = useState<AvailableReward[]>([]);
  const [loading, setLoading] = useState(true);
  const [claiming, setClaiming] = useState<string | null>(null);
  const [toast, setToast] = useState<string | null>(null);

  // SubmitURLModal state
  const [modalReward, setModalReward] = useState<SubmitURLModalReward | null>(null);
  const [modalSubmitting, setModalSubmitting] = useState(false);
  const [modalError, setModalError] = useState<string | null>(null);

  const load = useCallback(async () => {
    try {
      const res = await authFetch('/api/rewards/available');
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const body = await res.json();
      setRewards((body.data?.rewards ?? body.rewards ?? []) as AvailableReward[]);
    } catch (err) {
      console.error('Failed to load available rewards', err);
      setRewards([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  /**
   * POST the claim with the given verifier data shape.
   * Centralized so both the immediate-claim path and the modal-submit path
   * share toast handling, balance update, and reload logic.
   */
  const submitClaim = useCallback(async (
    reward: AvailableReward,
    data: Record<string, unknown>
  ): Promise<{ ok: boolean; reason?: string }> => {
    try {
      const res = await authFetch('/api/rewards/claim', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ rewardType: reward.rewardType, data }),
      });
      const body = await res.json();
      const result = body.data ?? body;

      if (res.ok && result.status === 'AWARDED') {
        updateBalance({ coins: userCoins + (result.coinsAwarded ?? 0) });
        setToast(`+${result.coinsAwarded}c — ${reward.label}`);
        setTimeout(() => setToast(null), 4000);
        await load();
        return { ok: true };
      }
      if (res.ok && result.status === 'PENDING') {
        setToast('Pending verification — check back in a few minutes');
        setTimeout(() => setToast(null), 4000);
        await load();
        return { ok: true };
      }

      const reason = body.error ?? result.reason ?? 'Claim failed';
      return { ok: false, reason };
    } catch {
      return { ok: false, reason: 'Network error' };
    }
  }, [load, updateBalance, userCoins]);

  const handleClaim = useCallback(async (reward: AvailableReward) => {
    if (claiming) return;

    const meta = readMetadata(reward.metadata);

    // URL-required reward → open modal, defer submission
    if (meta.requiresUrl) {
      setModalReward({
        rewardType: reward.rewardType,
        label: reward.label,
        description: reward.description,
        coinsAmount: reward.coinsAmount,
        platform: meta.platform ?? 'generic',
        composeUrl: meta.composeUrl,
        hint: meta.urlHint,
      });
      setModalError(null);
      return;
    }

    // No URL needed (e.g. milestone test, ad-watch claim) — submit immediately
    setClaiming(reward.rewardType);
    const result = await submitClaim(reward, {});
    if (!result.ok) {
      setToast(result.reason ?? 'Claim failed');
      setTimeout(() => setToast(null), 4000);
    }
    setClaiming(null);
  }, [claiming, submitClaim]);

  const handleModalSubmit = useCallback(async (postUrl: string) => {
    if (!modalReward) return;
    const reward = rewards.find((r) => r.rewardType === modalReward.rewardType);
    if (!reward) {
      setModalError('Reward unavailable');
      return;
    }
    setModalSubmitting(true);
    setModalError(null);
    const result = await submitClaim(reward, { postUrl });
    setModalSubmitting(false);
    if (result.ok) {
      setModalReward(null); // close on success
    } else {
      setModalError(result.reason ?? 'Claim failed');
    }
  }, [modalReward, rewards, submitClaim]);

  const handleModalClose = useCallback(() => {
    if (modalSubmitting) return;
    setModalReward(null);
    setModalError(null);
  }, [modalSubmitting]);

  if (loading) {
    return <div className={cn('rounded-lg border border-surface-3 bg-surface-2 p-3 h-24 animate-pulse', className)} />;
  }

  if (rewards.length === 0) {
    // Everything's inactive — don't show an empty card. The section simply hides.
    return null;
  }

  // Group by category, in our preferred render order
  const byCategory = new Map<string, AvailableReward[]>();
  for (const r of rewards) {
    const arr = byCategory.get(r.category) ?? [];
    arr.push(r);
    byCategory.set(r.category, arr);
  }

  return (
    <div className={cn('space-y-3', className)}>
      {toast && (
        <div className="px-3 py-2 rounded-lg bg-gold/15 border border-gold/30 text-[11px] text-gold font-semibold flex items-center gap-1.5">
          <CoinIcon className="w-3.5 h-3.5" /> {toast}
        </div>
      )}
      {CATEGORY_ORDER.map((cat) => {
        const items = byCategory.get(cat);
        if (!items || items.length === 0) return null;
        return (
          <div key={cat} className="rounded-lg border border-surface-3 bg-surface-2 p-3 space-y-1">
            <div className="text-xs font-bold text-cream mb-1">{CATEGORY_LABEL[cat] ?? cat}</div>
            {items.map((r) => (
              <RewardRow key={r.rewardType} reward={r} onClaim={handleClaim} claimingType={claiming} />
            ))}
          </div>
        );
      })}
      <SubmitURLModal
        reward={modalReward}
        open={modalReward !== null}
        submitting={modalSubmitting}
        errorMessage={modalError}
        onClose={handleModalClose}
        onSubmit={handleModalSubmit}
      />
    </div>
  );
}

export default RewardsList;
